/* eslint-disable @typescript-eslint/no-explicit-any */
import { Builder } from '@builder.io/sdk';
import { useEffect, useRef, useState } from 'react';

import loadMarketoScript from 'molecules/Form/utils/loadMarketoScript';
import removeMarketoFormStyles from 'molecules/Form/utils/removeMarketoFormStyles';
import truncatePlaceholders from 'molecules/Form/utils/truncatePlaceholders';

import { analyticsClient } from 'utils/analytics/client';
import { buildMarketoHiddenFields } from 'utils/attribution/marketoHidden';
import { appendUtmToUrl } from 'utils/attribution/utm';
import { trackBuilderConversion } from 'utils/builder/conversion';
import useBuilderVariantStore from 'utils/builder/useBuilderVariantStore';
import { CHILIPIPER_FORM_IDS as CHILIPIPER_FORM_IDS_LIST } from 'utils/chilipiper/formIds';
import { MARKETO_BASE_URL } from 'utils/marketo/config';
import { SUPPRESS_EDU_FORM_IDS, isEduEmail } from 'utils/marketo/eduSuppression';
import { SUPPRESS_FREE_EMAIL_FORM_IDS, isLikelyFreeEmail } from 'utils/marketo/freeEmailSuppression';
import { isSpamEmail } from 'utils/marketo/spamDomainSuppression';
import { getUrlObject } from 'utils/parse/parseUrl';
import { getSafePath } from 'utils/strings/slugs';
import { objectKeys } from 'utils/strings/typeUtils';
import useConvertingPageStore from 'utils/trackPage/store/useConvertingPageStore';

import type { RefObject } from 'react';

const SEGMENT_DEBUG_KEY = 'segment_form_debug_logs';

const CHILIPIPER_FORM_IDS = new Set<string>(CHILIPIPER_FORM_IDS_LIST);
const SUPPRESS_EDU_FORMS = new Set<string>(SUPPRESS_EDU_FORM_IDS);
const SUPPRESS_FREE_EMAIL_FORMS = new Set<string>(SUPPRESS_FREE_EMAIL_FORM_IDS);

const BUSINESS_EMAIL_ERROR_MESSAGE = 'Please use a business email address.';

type SegmentFormDebugEntry = {
  submittedAt: string;
  formId: string;
  email: string;
  url: string;
  path: string;
  segmentReady: boolean;
  rudderReady?: boolean;
  hasTrackingPreferencesCookie: boolean;
  phase: 'submit' | 'success';
};

const appendSegmentDebugLog = (entry: SegmentFormDebugEntry) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const raw = window.localStorage.getItem(SEGMENT_DEBUG_KEY);
    const current: SegmentFormDebugEntry[] = raw ? JSON.parse(raw) : [];

    const next = [...current, entry].slice(-500);

    window.localStorage.setItem(SEGMENT_DEBUG_KEY, JSON.stringify(next));
  } catch (error) {
    console.error('Failed to store Segment debug log', error);
  }
};

type RequestIdleCb = (cb: () => void, opts?: { timeout?: number }) => number;

type MarketoConfig = {
  formId: string;
  callback?: (ctx?: { values?: Record<string, any> }) => boolean | void;
  rootRef?: RefObject<HTMLElement>;
  rootMargin?: string;
  lockedContentPageId?: string;
  icon?: string;
  extraHiddenFields?: Record<string, string>;
};

const MUNCHKIN_ID = process.env.NEXT_PUBLIC_MUNCHKIN_ID || '';

const SPRITE_VERSION = process.env.NODE_ENV === 'development' ? Date.now() : 'v10';

const injectButtonIcon = (form: any, iconName: string) => {
  try {
    const $: any = (window as any).MktoForms2.$;
    const $form = $(form.getFormElem()[0] || form.getFormElem());
    const $btn = $form.find('button[type="submit"], .mktoButton').first();
    if (!$btn.length) {
      return;
    }

    const btn = $btn[0] as HTMLButtonElement;

    if (btn.querySelector('.mkto-btn-icon')) {
      return;
    }

    btn.style.display = 'inline-flex';
    btn.style.alignItems = 'center';
    btn.style.justifyContent = 'center';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '18');
    svg.setAttribute('height', '18');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.classList.add('mkto-btn-icon');
    svg.style.marginLeft = '0.5em';
    svg.style.flexShrink = '0';
    svg.style.fill = 'currentColor';
    svg.style.stroke = 'currentColor';

    const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    use.setAttributeNS('http://www.w3.org/1999/xlink', 'href', `/icons/sprites.svg?${SPRITE_VERSION}#${iconName}`);
    svg.appendChild(use);

    btn.appendChild(svg);
  } catch {
    /* no-op */
  }
};

// Global registry prevents duplicate Marketo loadForm calls across component instances
const globalFormLoaded = new Set<string>();

// Submission success is tracked per formId at module scope, not just in a single
// hook instance's state. A Marketo form can be torn down and re-rendered into a
// different React instance (responsive desktop/mobile swap, ClientOnly mount,
// parent re-render) between the time forms2 binds onSuccess and the time it
// fires. When that happens the instance whose onSuccess fired is not the one
// rendering the visible form, so the visible form keeps showing forms2's
// "Please Wait" and the success message never appears. Recording success here
// and notifying every live hook for that formId guarantees the visible form
// flips to the thank-you message regardless of which instance captured the lead.
const succeededForms = new Set<string>();
const successListeners = new Set<() => void>();

const markFormSucceeded = (id: string) => {
  succeededForms.add(id);
  successListeners.forEach(listener => {
    listener();
  });
};

const useMarketo = ({
  formId,
  callback,
  rootRef,
  rootMargin = '800px',
  lockedContentPageId,
  icon,
  extraHiddenFields,
}: MarketoConfig) => {
  const [successfulSubmit, setSuccessfulSubmit] = useState(() => succeededForms.has(formId));
  const [isLoading, setLoading] = useState(true);
  const formLoadedRef = useRef(false);
  const abPhase = useBuilderVariantStore(s => s.abPhase);

  // Reflect a success captured by any instance of this formId, including one
  // that fired between this hook's render and its effect, or in a sibling
  // instance after a remount.
  useEffect(() => {
    const syncSuccess = () => {
      if (succeededForms.has(formId)) {
        setSuccessfulSubmit(true);
      }
    };
    successListeners.add(syncSuccess);
    syncSuccess();

    return () => {
      successListeners.delete(syncSuccess);
    };
  }, [formId]);

  const loadForm = () => {
    try {
      if ((window as any)?.MktoForms2 && !formLoadedRef.current && !globalFormLoaded.has(formId)) {
        const foundForm = document.querySelector(`#mktoForm_${formId}.mktoForm`);
        if (foundForm) {
          setLoading(false);

          return;
        }

        formLoadedRef.current = true;
        globalFormLoaded.add(formId);

        (window as any).MktoForms2.loadForm(
          MARKETO_BASE_URL,
          MUNCHKIN_ID,
          formId,
          (form: {
            onSuccess: any;
            onSubmit: (cb: () => void) => void;
            getValues: () => any;
            addHiddenFields: (fields: Record<string, string>) => void;
          }) => {
            removeMarketoFormStyles(form);
            truncatePlaceholders(form);
            setLoading(false);

            if (icon) {
              injectButtonIcon(form, icon);
            }
            form.addHiddenFields(buildMarketoHiddenFields());

            if (extraHiddenFields) {
              form.addHiddenFields(extraHiddenFields);
            }

            // Spam-domain blocking runs on every form (universal), independent
            // of the per-campaign edu / free-email suppression which is keyed by
            // form ID. This is the in-browser layer on top of the Marketo-side
            // block so flagged domains never reach Marketo at all.
            const blocksEdu = SUPPRESS_EDU_FORMS.has(formId);
            const blocksFreeEmail = SUPPRESS_FREE_EMAIL_FORMS.has(formId);
            const formInstance = form as any;
            formInstance.onValidate?.(() => {
              const values = form.getValues();
              const email = values?.Email || values?.email || '';
              const failsSpam = isSpamEmail(email);
              const failsEdu = blocksEdu && isEduEmail(email);
              const failsFreeEmail = blocksFreeEmail && isLikelyFreeEmail(email);
              if (failsSpam || failsEdu || failsFreeEmail) {
                formInstance.submittable?.(false);
                const emailField = formInstance.getFormElem?.()?.find?.('#Email');
                if (emailField?.length) {
                  formInstance.showErrorMessage?.(BUSINESS_EMAIL_ERROR_MESSAGE, emailField);
                }
              }
            });

            form.onSubmit(() => {
              form.addHiddenFields(buildMarketoHiddenFields());

              if (extraHiddenFields) {
                form.addHiddenFields(extraHiddenFields);
              }

              const currentPath = getSafePath();

              form.addHiddenFields({
                Lead_Source_Detail__c: currentPath,
                'Conversion Mechanism': currentPath,
                Conversion_Mechanism__c: currentPath,
              });

              const builderVariantId = useBuilderVariantStore.getState().variantId;
              if (builderVariantId) {
                form.addHiddenFields({ Builder_Test_Variant__c: builderVariantId });
              }

              if (lockedContentPageId) {
                const now = new Date().toISOString();
                form.addHiddenFields({
                  Last_Unlocked_Content__c: lockedContentPageId,
                  Last_Unlocked_At__c: now,
                });
              }

              const previousPath = useConvertingPageStore.getState().previousPath;
              if (previousPath) {
                form.addHiddenFields({
                  'Converting Page': previousPath,
                  Converting_Page__c: previousPath,
                });
              }
            });

            form.onSubmit(() => {
              const identityFields = form.getValues();

              const segmentReady = analyticsClient.isSegmentReady();
              const rudderReady = analyticsClient.isRudderReady();

              appendSegmentDebugLog({
                submittedAt: new Date().toISOString(),
                formId,
                email: identityFields.Email || identityFields.email || '',
                url: window.location.href,
                path: window.location.pathname + window.location.search,
                segmentReady,
                rudderReady,
                hasTrackingPreferencesCookie: document.cookie.includes('tracking-preferences'),
                phase: 'submit',
              });
            });

            form.onSuccess((_: any, redirect: string) => {
              markFormSucceeded(formId);
              trackBuilderConversion();
              const identityFields = form.getValues();

              delete identityFields.formid;
              delete identityFields.formId;
              delete identityFields.munchkinId;
              delete identityFields.munchkinid;
              useConvertingPageStore.getState().setPreviousPath('');
              sessionStorage.removeItem('previousPath');

              const segmentReady = analyticsClient.isSegmentReady();
              const rudderReady = analyticsClient.isRudderReady();

              appendSegmentDebugLog({
                submittedAt: new Date().toISOString(),
                formId,
                email: identityFields.Email || identityFields.email || '',
                url: window.location.href,
                path: window.location.pathname + window.location.search,
                segmentReady,
                rudderReady,
                hasTrackingPreferencesCookie: document.cookie.includes('tracking-preferences'),
                phase: 'success',
              });

              const email = identityFields.Email || identityFields.email;

              if (email) {
                analyticsClient.identify(email, identityFields);
              }

              // Segment's bundled "Google Tag Manager" destination (GTM-KB7SNHC,
              // device mode) already pushes every track() to the dataLayer as
              // { event: <name>, ...props }, so this track fires GTM's
              // "ALL - Form Submission" Custom Event trigger. Do NOT also push
              // form_submission to the dataLayer manually here: that double-fires
              // the trigger (and the Google Ads conversion). The GTM bridge is the
              // Segment destination, not a direct push.
              analyticsClient.track('form_submission', { ...identityFields, formId });
              analyticsClient.track(`Submitted Form - ${formId}`, identityFields);

              // Dreamdata manual form tracking. Marketo forms2 intercepts the
              // native submit and posts over AJAX, so Dreamdata's automatic HTML
              // form tracking (`formTracking:{html:true}` in the init snippet)
              // never sees these submissions and the conversions go untracked,
              // exactly like the Wisepops case handled in layout/Head. Fire
              // identify + track manually here, once, so every Marketo form is
              // covered without touching forms individually. `track`/`identify`
              // are queue stubs, so calling before the CDN script loads is safe.
              const dreamdata = (window as any).dreamdata;
              if (dreamdata?.track) {
                if (email) {
                  dreamdata.identify(null, { email });
                }
                dreamdata.track('Form Submitted', { source: 'marketo', formId });
              }

              if (callback) {
                const reloadPage = callback({ values: identityFields });
                if (reloadPage !== undefined) {
                  return reloadPage || false;
                }
              }

              if (formId === '637') {
                sessionStorage.setItem('build-vs-buy-quiz-from-form', 'true');
                sessionStorage.setItem('build-vs-buy-quiz-session', 'active');

                const emailToSave = identityFields.Email || identityFields.email;
                if (emailToSave) {
                  sessionStorage.setItem('build-vs-buy-quiz-user-email', emailToSave);
                } else {
                  console.error('[Form 637] ❌ NO EMAIL found in identityFields!');
                }

                if (redirect) {
                  setTimeout(() => {
                    try {
                      const redirectObj = getUrlObject(redirect);
                      const currentUrl = getUrlObject(window.location.href);

                      if (redirectObj?.host === currentUrl?.host && redirectObj?.pathname === currentUrl?.pathname) {
                        return;
                      }

                      objectKeys(identityFields).forEach(key => {
                        typeof key === 'string' && redirectObj?.searchParams.set(key, identityFields[key]);
                      });

                      window.location.href = redirectObj?.toString() || 'https://prismatic.io';
                    } catch {
                      console.error('[Form 637] ❌ Error redirecting to quiz results page');
                    }
                  }, 150);

                  return false; // Prevent default Marketo redirect
                }
              }

              if (CHILIPIPER_FORM_IDS.has(formId)) {
                const chilipiper = (window as any).ChiliPiper;
                if (chilipiper?.submit) {
                  chilipiper.submit('prismatic', 'ae-intro-call-router', {
                    map: true,
                    lead: identityFields,
                    onRouted: () => {
                      // Keep user on the current page after routing completes
                    },
                    onSuccess: () => {
                      // Keep user on the current page after booking completes
                    },
                    onClose: () => {
                      // Keep user on the current page if they close the popup
                    },
                  });
                } else {
                  console.warn(
                    '[ChiliPiper] window.ChiliPiper.submit not available when form',
                    formId,
                    'was submitted',
                  );
                }

                return false;
              }

              if (redirect) {
                try {
                  // Only the campaign travels to the thank-you page, never the
                  // submitted values. Copying every form field onto the URL put
                  // the visitor's name, email and company in front of every tag
                  // on that page (GTM, LinkedIn, OpenAI, Dreamdata) and into
                  // analytics page URLs, and no thank-you page reads them. The
                  // lead is already saved in Marketo by the time this runs.
                  const redirectObj = getUrlObject(appendUtmToUrl(redirect));
                  const currentUrl = getUrlObject(window.location.href);

                  if (redirectObj?.host === currentUrl?.host && redirectObj?.pathname === currentUrl?.pathname) {
                    return false;
                  }

                  window.location.href = redirectObj?.toString() || 'https://prismatic.io';
                } catch {
                  return false;
                }
              }

              return false;
            });
          },
        );
      }
    } catch (error) {
      console.error('Failed to load Marketo form:', error);
      setLoading(false);
    }
  };

  const loadNow = () => {
    if (formLoadedRef.current || globalFormLoaded.has(formId)) {
      setLoading(false);

      return;
    }
    loadMarketoScript()
      .then(() => loadForm())
      .catch(err => {
        console.error('Failed to load Marketo script:', err);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (Builder.isEditing) {
      loadNow();

      return () => {
        globalFormLoaded.delete(formId);
      };
    }

    // Do not arm while a client-side A/B variant swap is still resolving. The
    // parent variation mounted now is replaced on swap, and forms2 does not
    // re-bind onSuccess on a second loadForm for the same id, so a form loaded
    // against the throwaway parent submits but its success never fires (stuck on
    // "Please Wait", no thank-you). Arming only once the swap settles makes
    // forms2 load exactly once, on the final variation. Re-runs when abPhase
    // leaves 'pending'.
    if (abPhase === 'pending') {
      return;
    }

    const target =
      rootRef?.current ?? document.querySelector('[data-mkto-root]') ?? document.getElementById('mkto-form-block');
    if (!target) {
      return;
    }

    let loaded = false;
    let observer: IntersectionObserver | null = null;
    let idleHandle: number | null = null;
    const interactionEvents: (keyof DocumentEventMap)[] = [
      'focusin',
      'pointerdown',
      'touchstart',
      'mouseenter',
      'keydown',
    ];
    let cleanup: () => void = () => {};

    const triggerLoad = () => {
      if (loaded) {
        return;
      }
      loaded = true;
      cleanup();
      loadNow();
    };

    // forms2.min.js executes ~340ms of script plus forced reflows, so it is
    // deferred (not loaded inline) via the proactive load orchestrators
    // (intersection observer + idle fallback) armed below. Direct form touches
    // load immediately via the target-level listeners below, so a user who
    // goes straight for the form never waits on the observer path.
    const earlyTrigger = (ev: Event) => {
      ev?.preventDefault?.();
      triggerLoad();
    };
    interactionEvents.forEach(ev => {
      target.addEventListener(ev, earlyTrigger, { once: true, passive: true, capture: true });
    });

    const setupDeferredTriggers = () => {
      if (loaded) {
        return;
      }

      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(
          entries => {
            if (entries.some(e => e.isIntersecting)) {
              triggerLoad();
            }
          },
          { rootMargin },
        );
        observer.observe(target);
      }

      const ric = (typeof window !== 'undefined' ? (window as any).requestIdleCallback : undefined) as
        RequestIdleCb | undefined;
      if (ric) {
        idleHandle = ric(triggerLoad, { timeout: 2500 }) as unknown as number;
      } else {
        idleHandle = window.setTimeout(triggerLoad, 2000);
      }
    };

    // Load proactively on mount: arm the IntersectionObserver + idle fallback
    // immediately instead of waiting for a user interaction. A visitor who
    // never moves the cursor or scrolls must still get the form, otherwise an
    // above-the-fold form never materializes (reported: forms not loading when
    // the cursor is totally still). This trades the PSI/Lighthouse benefit of
    // interaction-gating for reliable form loading. Direct form touches still
    // load immediately via the target-level listeners above.
    setupDeferredTriggers();

    cleanup = () => {
      interactionEvents.forEach(ev => {
        target.removeEventListener(ev, earlyTrigger, { capture: true } as EventListenerOptions);
      });
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (idleHandle !== null) {
        const cancel = (window as any).cancelIdleCallback as ((h: number) => void) | undefined;
        if (cancel) {
          cancel(idleHandle);
        } else {
          window.clearTimeout(idleHandle);
        }
        idleHandle = null;
      }
    };

    return () => {
      cleanup();
      globalFormLoaded.delete(formId);
    };
  }, [rootRef, rootMargin, abPhase]);

  return { isLoading, successfulSubmit };
};

export default useMarketo;
